// React / Redux
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components JSX
import SvgIcon from '../../../common/svg-icon';
import { setQuoteDetails } from '../../../data/slices/quote-details-slice';
import useTranslations from '../../../hooks/use-translations';
import { useQuoteToolContext } from '../../../quote-tool';

export default function OptionCard({
    option,
    setInfoWindow,
    setLastClickedElement
}) {
    // Translations
    const globalTranslations = useTranslations('GLOBAL');

    const quoteDetails = useSelector((store) => store.quoteDetails);
    const quoteDetailsDispatch = useDispatch();
    const selectedOption = quoteDetails?.MARQUEE_OPTIONS?.find(
        (optionToCheck) => optionToCheck.option_id === option.option_id
    );

    const isQuantity = option.option_quantity === 'true';
    const [quantitySelected, setQuantitySelected] = useState(
        selectedOption?.qty || 0
    );
    const [showMaxError, setShowMaxError] = useState(false);

    const { quoteToolRef } = useQuoteToolContext();

    const linenClothRef = useRef();

    const handleOptionSelection = useCallback(
        (e) => {
            e?.stopPropagation();

            if (isQuantity && quantitySelected === 0) {
                return;
            }

            if (selectedOption) {
                quoteDetailsDispatch(
                    setQuoteDetails({
                        MARQUEE_OPTIONS:
                            quoteDetails?.MARQUEE_OPTIONS?.filter(
                                (optionToCheck) =>
                                    optionToCheck.option_id !== option.option_id
                            ) || [],
                        OPTION_COUNT: quoteDetails?.OPTION_COUNT - 1
                    })
                );
            } else {
                quoteDetailsDispatch(
                    setQuoteDetails({
                        MARQUEE_OPTIONS: [
                            ...(quoteDetails?.MARQUEE_OPTIONS || []),
                            {
                                ...option,
                                ...(isQuantity
                                    ? {
                                          qty: quantitySelected,
                                          addLinenCloth:
                                              linenClothRef?.current?.checked
                                      }
                                    : {})
                            }
                        ],
                        OPTION_COUNT: quoteDetails?.OPTION_COUNT + 1
                    })
                );
            }
        },
        [
            isQuantity,
            quantitySelected,
            selectedOption,
            quoteDetailsDispatch,
            quoteDetails?.MARQUEE_OPTIONS,
            quoteDetails?.OPTION_COUNT,
            option
        ]
    );

    const handleMoreInfo = (e) => {
        e.stopPropagation();

        setLastClickedElement(e.target);
        setInfoWindow(option.option_description?.replace(/\\'/g, "'"));

        quoteToolRef.current?.scrollIntoView();
    };

    useEffect(() => {
        if (selectedOption?.qty) {
            setQuantitySelected(selectedOption?.qty);
        }
    }, [selectedOption]);

    return (
        <div
            className={`option-card ${
                selectedOption ? 'option-card--selected' : ''
            }`}
            onClick={!isQuantity ? handleOptionSelection : null}
            role="button"
        >
            <h3 className="option-card__name">
                {option.option_name.replace(/\\'/g, "'")}
                {option.option_description && (
                    <button
                        className="option-card__more-info"
                        onClick={handleMoreInfo}
                    >
                        i
                    </button>
                )}
            </h3>

            {isQuantity && (
                <>
                    <QuantityField
                        initialValue={quantitySelected}
                        maxQuantity={
                            parseInt(option.option_max_quantity) || Infinity
                        }
                        setShowMaxError={setShowMaxError}
                        onUpdate={(qty) => {
                            setQuantitySelected(qty);

                            // Handle the case where quantity is set to 0 (removing option)
                            if (selectedOption && qty === 0) {
                                quoteDetailsDispatch(
                                    setQuoteDetails({
                                        MARQUEE_OPTIONS:
                                            quoteDetails?.MARQUEE_OPTIONS?.filter(
                                                (optionToCheck) =>
                                                    optionToCheck.option_id !==
                                                    option.option_id
                                            ) || [],
                                        OPTION_COUNT:
                                            quoteDetails?.OPTION_COUNT - 1
                                    })
                                );
                            }
                            // Handle the case where quantity is set to 1 (adding option)
                            else if (qty === 1) {
                                quoteDetailsDispatch(
                                    setQuoteDetails({
                                        MARQUEE_OPTIONS: [
                                            ...(quoteDetails?.MARQUEE_OPTIONS?.filter(
                                                (optionToCheck) =>
                                                    optionToCheck.option_id !==
                                                    option.option_id
                                            ) || []),
                                            {
                                                ...option,
                                                qty: 1,
                                                addLinenCloth:
                                                    linenClothRef?.current
                                                        ?.checked
                                            }
                                        ],
                                        ...(!selectedOption && {
                                            OPTION_COUNT:
                                                quoteDetails?.OPTION_COUNT + 1
                                        })
                                    })
                                );
                            }
                            // Handle the case where quantity is greater than 1 (updating quantity)
                            else if (qty > 1) {
                                const existingOptionIndex =
                                    quoteDetails?.MARQUEE_OPTIONS?.findIndex(
                                        (optionToCheck) =>
                                            optionToCheck.option_id ===
                                            option.option_id
                                    );

                                // If the option exists, update it; if not, add it
                                quoteDetailsDispatch(
                                    setQuoteDetails({
                                        MARQUEE_OPTIONS:
                                            existingOptionIndex >= 0
                                                ? quoteDetails.MARQUEE_OPTIONS?.map(
                                                      (optionToCheck, index) =>
                                                          index ===
                                                          existingOptionIndex
                                                              ? {
                                                                    ...optionToCheck,
                                                                    qty,
                                                                    addLinenCloth:
                                                                        linenClothRef
                                                                            ?.current
                                                                            ?.checked
                                                                }
                                                              : optionToCheck
                                                  )
                                                : [
                                                      ...(quoteDetails.MARQUEE_OPTIONS ||
                                                          []),
                                                      {
                                                          ...option,
                                                          qty,
                                                          addLinenCloth:
                                                              linenClothRef
                                                                  ?.current
                                                                  ?.checked
                                                      }
                                                  ]
                                    })
                                );
                            }
                        }}
                    />

                    {/* Render linen cloth checkbox only if quantity > 0 and option allows linen cloth */}
                    {quantitySelected > 0 &&
                        option.option_linen_cloth === 'true' && (
                            <label className="option-card__checkbox">
                                <input
                                    ref={linenClothRef}
                                    type="checkbox"
                                    onChange={() => {
                                        // Dispatch update when linen cloth checkbox is toggled
                                        quoteDetailsDispatch(
                                            setQuoteDetails({
                                                MARQUEE_OPTIONS:
                                                    quoteDetails?.MARQUEE_OPTIONS?.map(
                                                        (optionToCheck) => {
                                                            if (
                                                                optionToCheck.option_id ===
                                                                option.option_id
                                                            ) {
                                                                return {
                                                                    ...optionToCheck,
                                                                    addLinenCloth:
                                                                        linenClothRef
                                                                            .current
                                                                            .checked
                                                                };
                                                            }
                                                            return optionToCheck;
                                                        }
                                                    ) || []
                                            })
                                        );
                                    }}
                                />
                                <div>
                                    <span>{globalTranslations?.ADD_LINEN}</span>
                                    <span>
                                        £{option.option_linen_cloth_price}{' '}
                                        {globalTranslations?.EACH}
                                    </span>
                                </div>
                            </label>
                        )}

                    <small
                        style={{
                            color: 'var(--colour-error)',
                            fontWeight: 'bold',
                            fontSize: '12px'
                        }}
                    >
                        {showMaxError
                            ? `*Max quantity ${option?.option_max_quantity}`
                            : ''}
                    </small>
                </>
            )}

            <div className="option-card__select-button">
                <p className="option-card__select-button-price">
                    £{option.option_price}
                </p>

                {selectedOption ? (
                    <>
                        {isQuantity ? (
                            <p className="option-card__select-button-text">
                                {selectedOption.qty || quantitySelected}{' '}
                                {globalTranslations?.SELECTED}
                            </p>
                        ) : (
                            <SvgIcon
                                name="tick"
                                cssClass="option-card__select-button-icon"
                            />
                        )}
                    </>
                ) : !isQuantity ? (
                    <p
                        className={`option-card__select-button-text ${
                            isQuantity && quantitySelected === 0
                                ? 'option-card__select-button-text--disabled'
                                : ''
                        }`}
                    >
                        {globalTranslations?.SELECT}
                    </p>
                ) : (
                    <p
                        className={`option-card__select-button-text ${
                            isQuantity && quantitySelected === 0
                                ? 'option-card__select-button-text--disabled'
                                : ''
                        }`}
                    ></p>
                )}
            </div>
        </div>
    );
}

const QuantityField = ({
    initialValue,
    onUpdate,
    setShowMaxError,
    maxQuantity
}) => {
    const [quantity, setQuantity] = useState(initialValue);

    const handleIncrement = useCallback(
        (evt) => {
            evt.stopPropagation();

            if (!isNaN(quantity) && quantity <= maxQuantity) {
                setShowMaxError(false);
            } else if (!isNaN(quantity) && quantity > maxQuantity) {
                setShowMaxError(true);
            } else if (isNaN(quantity)) {
                setShowMaxError(false);
            }

            if (quantity < maxQuantity) {
                setQuantity((prevQuantity) => prevQuantity + 1);
                onUpdate(quantity + 1);
            }
        },
        [maxQuantity, onUpdate, quantity, setShowMaxError]
    );

    const handleDecrement = useCallback(
        (evt) => {
            evt.stopPropagation();

            if (!isNaN(quantity) && quantity <= maxQuantity) {
                setShowMaxError(false);
            } else if (!isNaN(quantity) && quantity > maxQuantity) {
                setShowMaxError(true);
            } else if (isNaN(quantity)) {
                setShowMaxError(false);
            }

            if (quantity >= 1) {
                setQuantity((prevQuantity) => prevQuantity - 1);
                onUpdate(quantity - 1);
            }
        },
        [maxQuantity, onUpdate, quantity, setShowMaxError]
    );

    const handleChange = (evt) => {
        evt.stopPropagation();

        const newQuantity = parseInt(evt.target.value);

        if (!isNaN(newQuantity) && newQuantity <= maxQuantity) {
            setQuantity(parseInt(newQuantity, 10));
            onUpdate(parseInt(newQuantity, 10));
            setShowMaxError(false);
        } else if (!isNaN(newQuantity) && newQuantity > maxQuantity) {
            setShowMaxError(true);
        } else if (isNaN(newQuantity)) {
            setQuantity(0);
            onUpdate(0);
            setShowMaxError(false);
        }
    };

    const handleInputClick = (e) => {
        e.stopPropagation();
    };

    return (
        <div className="option-card__quantity-field">
            <button onClick={handleDecrement}>
                <SvgIcon name="minus" />
            </button>
            <input
                type="number"
                value={initialValue || quantity || 0}
                onChange={handleChange}
                onClick={handleInputClick}
            />
            <button
                onClick={handleIncrement}
                disabled={quantity === maxQuantity}
            >
                <SvgIcon name="add" />
            </button>
        </div>
    );
};
